import React, { useContext, useEffect, useState, useRef } from 'react';
import axios from "axios";
import { Link, navigate, useQueryParams } from 'raviger';
import { Button, ButtonGroup, Card, CardGroup, Col, Form, FormControl, InputGroup, ListGroup, OverlayTrigger, Pagination, Row, Spinner, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import {
  faDotCircle
} from '@fortawesome/free-regular-svg-icons';
import { useMark, useSubmitting } from '../hooks';
import Header from '../components/Header';
import { titleCase } from '../components/Utils';
import Scrollbar from '../components/Scrollbars';
import { ITEMS_PER_PAGE } from '../constants';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import { printAllOwnersDetails } from './Utils';
import LoadingLink from "../components/LoadingLink";
import ActionsDropdown from '../components/ActionsDropdown';

function PersonSearch({ incident, organization }) {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

	// Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    search = '',
    person = 'owners',
  } = queryParams;

	const [data, setData] = useState({owners: [], total_count: 0, isFetching: false});
  const [organizationData, setOrganizationData] = useState({
    name: '',
    short_name: '',
    liability_name: '',
    liability_short_name: '',
  });
	const [searchState, setSearchState] = useState({});
	const [statusOptions, setStatusOptions] = useState(person);
	const [searchTerm, setSearchTerm] = useState(search);
	const tempSearchTerm = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  // const [triggerRefresh, setTriggerRefresh] = useState(false);
	const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [goToID, setGoToID] = useState('');
	const { markInstances } = useMark();
  const {
    handleSubmitting,
    submittingComplete,
  } = useSubmitting();

  // Update searchTerm when field input changes.
  const handleChange = event => {
    tempSearchTerm.current.value = event.target.value;
  };

  const handleIDChange = async event => {
    setGoToID(event.target.value);
  };

  // Use searchTerm to filter owners.
  const handleSubmit = async event => {
    event.preventDefault();
    setSearchTerm(tempSearchTerm.current.value);
		setPage(1);
  }

  const handlePrintAllClick = async e => {
    setIsLoading(true);
    let owners = [];

    // Fetch all owners data.
    let nextUrl = '/people/api/person/?page_size=1&page=1&search=' + searchTerm + '&status=' + statusOptions + '&incident=' + incident + '&organization=' + organization +'&training=' + (state && state.incident.training);
    do {
      const response = await axios.get(nextUrl, {
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
      });

      owners.push(...response.data.results);
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/people/' + response.data.next.split('/people/')[1];
      }
    } while(nextUrl != null)

    handleSubmitting()
      .then(() => printAllOwnersDetails(owners, organizationData))
      .then(submittingComplete)
      .finally(() => setIsLoading(false));
  }

	function setFocus(pageNum) {
    if (pageNum !== page) {
      tempSearchTerm.current.focus();
    }
  }

  	// Hook for initializing data.
	useEffect(() => {
		let unmounted = false;
		let source = axios.CancelToken.source();

    const fetchOrganizationData = async () => {
      // Fetch Organization data.
      await axios.get('/incident/api/organization/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setOrganizationData(response.data[0]);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchOrganizationData();

		// Cleanup.
		return () => {
			unmounted = true;
			source.cancel();
		};
	}, []);

	// Hook for initializing data.
	useEffect(() => {
		let unmounted = false;
		let source = axios.CancelToken.source();

		const fetchOwners = () => {
			setData({owners: [], total_count: 0, isFetching: true});
			// Fetch People data.
			axios.get('/people/api/person/?page=' + page + '&search=' + searchTerm + '&status=' + statusOptions + '&incident=' + incident + '&organization=' + organization +'&training=' + (state && state.incident.training), {
				cancelToken: source.token,
			})
			.then(response => {
				if (!unmounted) {
					setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
					setData({owners: response.data.results, total_count:response.data.count, isFetching: false});
					let search_state = {};
					response.data.results.forEach(owner => {
						let species = [];
            if (statusOptions === 'owners') {
              owner.animals.forEach(animal => {
                if (!species.includes(animal.species_string_singular)) {
                  species.push(animal.species_string_singular)
                }
              })
            }
						else {
              owner.reporter_animals.forEach(animal => {
                if (!species.includes(animal.species_string_singular)) {
                  species.push(animal.species_string_singular)
                }
              })
            }
            species.sort(function(a, b) {
              return a.localeCompare(b);
            });
						search_state[owner.id] = {species:species, selectedSpecies:species[0]}
						})
					setSearchState(search_state);

					// highlight search terms
          markInstances(searchTerm);
				}
			})
			.catch(error => {
				if (!unmounted) {
					setData({owners: [], isFetching: false});
          setShowSystemError(true);
				}
			});
		};
    fetchOwners();

		// Cleanup.
		return () => {
			unmounted = true;
			source.cancel();
		};
	}, [searchTerm, statusOptions, incident, page]);

	return (
    <div className="ms-2 me-2">
    <Header>Search Owners & Reporters</Header>
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col xs="2" style={{maxWidth:"150px", marginRight:"-10px", paddingRight:"0px"}}>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="ID #"
              name="searchIDTerm"
              onChange={handleIDChange}
            />
             <Button variant="outline-light" type="submit" disabled={!goToID} style={{borderRadius:"0 5px 5px 0", color:"white"}} onClick={(e) => {navigate("/" + organization + "/" + incident + "/people/owner/" + goToID)}}>Go</Button>
          </InputGroup>
        </Col>
        <Col>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="Search"
              name="searchTerm"
              onChange={handleChange}
              ref={tempSearchTerm}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              />
              <Button variant="outline-light" type="submit" style={{borderRadius:"0 5px 5px 0", color:"white"}}>Search
                <OverlayTrigger
                  key={"search-information"}
                  placement="right"
                  overlay={
                    <Tooltip id={`tooltip-search-information`}>
                      Searchable fields: name, phone number, email, drivers license, address fields, evacuation zone, and animal names.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
                </OverlayTrigger>
              </Button>
            <ButtonGroup className="ms-1 me-1 border rounded" style={{height:"38px"}}>
              <Button variant={statusOptions === "owners" ? "primary" : "secondary"} onClick={statusOptions !== "owners" ? () => {setPage(1);setStatusOptions("owners")} : null} style={{paddingTop:"5px"}}>Owners</Button>
              <Button variant={statusOptions === "reporters" ? "primary" : "secondary"} onClick={statusOptions !== "reporters" ? () => {setPage(1);setStatusOptions("reporters")} : null} style={{paddingTop:"5px"}}>Reporters</Button>
            </ButtonGroup>
            {isLoading ? (
              <div className="d-flex" style={{width:"148px", justifyContent:"center"}}>
                <Spinner
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '2rem',
                      width: '2rem',
                      marginBottom: '0.25rem'
                    }
                  }}
                />
              </div>
            ) : (
              <ActionsDropdown variant="outline-light" title={"Download All" + " (" + `${data.total_count}` + ")"} search={true} disabled={data.isFetching || data.owners.length === 0}>
                <LoadingLink onClick={() => handlePrintAllClick()} isLoading={data.isFetching} className="d-block" label="Owner Summaries">
                  <FontAwesomeIcon icon={faFilePdf} inverse />
                </LoadingLink>
              </ActionsDropdown>
            )}
          </InputGroup>
        </Col>
      </Row>
    </Form>
    {data.owners.map((owner, index) => (
      <div key={owner.id} className="mt-3 border rounded">
        <div className="card-header"> {owner.first_name ?
          <h4 style={{marginBottom: "-2px",  marginLeft:"-12px"}}>
            {statusOptions === 'owners' ?
            <OverlayTrigger
              key={"owner-details"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-owner-details`}>
                  Owner details
                </Tooltip>
              }
            >
              <Link href={"/" + organization + "/" + incident + "/people/owner/" + owner.id}><FontAwesomeIcon icon={faDotCircle} className="me-2" inverse/></Link>
            </OverlayTrigger>
            :
            <OverlayTrigger
              key={"reporter-details"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-reporter-details`}>
                  Reporter details
                </Tooltip>
              }
            >
              <Link href={"/" + organization + "/" + incident + "/people/reporter/" + owner.id}><FontAwesomeIcon icon={faDotCircle} className="me-2" inverse/></Link>
            </OverlayTrigger>
            }
            {owner.first_name} {owner.last_name}
            {/* {owner.agency ? <span> ({owner.agency})</span> : ""} */}
          </h4> : "Unknown Name"}
        </div>
        <CardGroup style={{marginBottom:"-6px"}}>
          <Card style={{marginBottom:"6px"}}>
            <Card.Body>
              <Card.Title style={{marginTop:"-9px", marginBottom:"8px"}}>Information</Card.Title>
              <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                <ListGroup>
                  <ListGroup.Item><b>Phone: </b>{owner.phone ? <span>{owner.display_phone} </span> : "None"}</ListGroup.Item>
                  <ListGroup.Item><b>Email: </b>{owner.email ? <span>{owner.email} </span> : "None"}</ListGroup.Item>
                  {owner.full_address ?
                    <ListGroup.Item><b>Address: </b>{owner.full_address}</ListGroup.Item>
                  : ""}
                  {/* {owner.requests && owner.requests.map(request => (
                    <ListGroup.Item key={request.id}><b>SR#{request.id_for_incident}: </b><Link href={"/" + organization + "/" + incident + "/hotline/servicerequest/" + request.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>{request.full_address}</Link></ListGroup.Item>
                  ))} */}
                  <ListGroup.Item><b>Evac Zone: </b>{owner.evac_zone ? <span>{owner.evac_zone} </span> : "None"}</ListGroup.Item>
                </ListGroup>
               
              </Scrollbar>
            </Card.Body>
          </Card>
          {searchState[owner.id] ?
          <Card style={{marginBottom:"6px"}}>
            <Card.Body style={{width:"525px"}}>
              <Card.Title style={{marginTop:"-10px", marginBottom:"12px"}}>
                <Scrollbar horizontal="true" autoHide style={{height:"32px", width:"485px"}} renderView={props => <div {...props} style={{...props.style, marginBottom:"-18px", marginRight:"0px", overflowX:"auto", overflowY: "hidden"}}/>} renderThumbVertical={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  <ListGroup horizontal className="ps-0 pe-0 d-flex" style={{display:"inline-block", whiteSpace:"nowrap"}}>
                  {searchState[owner.id].species.map(species => (
                    <ListGroup.Item key={species} active={searchState[owner.id].selectedSpecies === species ? true : false} style={{textTransform:"capitalize", cursor:'pointer', paddingTop:"4px", paddingBottom:"4px", zIndex:"10000"}} onClick={() => setSearchState(prevState => ({ ...prevState, [owner.id]:{...prevState[owner.id], selectedSpecies:species} }))}>{species}</ListGroup.Item>
                  ))}
                  </ListGroup>
                </Scrollbar>
              </Card.Title>
              {statusOptions === 'owners' ?
              <ListGroup style={{height:"159px", overflowY:"auto", marginTop:"-12px"}}>
                <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  {owner.animals.filter(animal => animal.species_string_singular === searchState[owner.id].selectedSpecies).map((animal, i) => (
                    <ListGroup.Item key={animal.id} className="rounded">
                      <b><Link href={"/" + organization + "/" + incident + "/animals/" + animal.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>#{animal.id_for_incident}</Link>:</b>&nbsp;&nbsp;{Number(animal.animal_count) === 1 ? <span>{animal.name || "Unknown"}</span> : <span>{animal.animal_count} {titleCase(animal.species_string_singular)}</span>}
                      &nbsp;- {animal.status}
                    </ListGroup.Item>
                  ))}
                  {owner.animals.length < 1 ? <ListGroup.Item>No Animals</ListGroup.Item> : ""}
                </Scrollbar>
              </ListGroup> : ""}
              {statusOptions === 'reporters' ?
              <ListGroup style={{height:"159px", overflowY:"auto", marginTop:"-12px"}}>
                <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  {owner.reporter_animals.filter(animal => animal.species_string_singular === searchState[owner.id].selectedSpecies).map((animal, i) => (
                    <ListGroup.Item key={animal.id}>
                      <b>A#{animal.id_for_incident}:</b>&nbsp;&nbsp;<Link href={"/" + organization + "/" + incident + "/animals/" + animal.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>{animal.name || "Unknown"}</Link>
                      &nbsp;- {animal.status}
                    </ListGroup.Item>
                  ))}
                  {owner.reporter_animals.length < 1 ? <ListGroup.Item style={{}}>No Animals</ListGroup.Item> : ""}
                </Scrollbar>
              </ListGroup> : ""}
            </Card.Body>
          </Card>
          : ""}
        </CardGroup>
      </div>
    ))}
    <p className="mt-3">{data.isFetching ? 'Fetching ' + statusOptions + '...' :
      <span>{data.owners && data.owners.length ? '' : 'No ' + statusOptions + ' found.'}</span>}
    </p>
    <Pagination className="custom-page-links" size="lg" onClick={(e) => {setFocus(parseInt(e.target.innerText));setPage(parseInt(e.target.innerText))}}>
      {[...Array(numPages).keys()].map(x =>
      <Pagination.Item key={x+1} active={x+1 === page}>
        {x+1}
      </Pagination.Item>)
      }
    </Pagination>
	</div>
	)
}

export default PersonSearch;
